import * as React from 'react';
import {
    ContextualMenu,
    DefaultButton,
    Dialog,
    DialogFooter,
    DialogType,
    IContextualMenuProps,
    PrimaryButton,
} from '@fluentui/react';
import { defaultKunde, useKunde } from 'api/kunde';
import { LoadingIndicator } from 'layout/LoadingIndicator/LoadingIndicator';
import moment from 'moment-timezone';
import { cloneDeep, isEqual } from 'lodash';
import { SavingState } from 'api/savingState';
import { useIsMounted } from 'hooks/useIsMounted';
import {
    addLieferantenrechnung,
    copyLieferantenrechnung,
    deleteLieferantenrechnung,
    getDefaultLieferantenrechnung,
    Lieferantenrechnung,
    recalculateLieferantenrechnung,
    updateLieferantenrechnung,
    useLieferantenrechnung,
} from 'api/lieferantenrechnung';
import { formStyles } from 'styles/formStyles';
import { FormTextField, FormTextFieldOptions } from 'formComponents/FormTextField';
import { Timestamps } from 'formComponents/Timestamps';
import { FormTextarea } from 'formComponents/FormTextarea';
import { LieferantenrechnungStatus } from 'formComponents/lookups/LieferantenrechnungStatus';
import { FormDatePicker } from 'formComponents/FormDatePicker';
import { KundeSelect } from 'components/Kunde/KundeSelect';
import { createKundeUrl, formatMoney } from 'utils/formatter';
import { useLieferantenrechnungPositionList } from 'api/lieferantenrechnungPosition';
import { ConfirmMessage } from 'formComponents/ConfirmMessage/ConfirmMessage';
import { OpenLinkIcon } from 'formComponents/OpenLinkIcon/OpenLinkIcon';
import { LieferantenrechnungStatusLabel } from 'utils/labels';
import { FormDecimalField, FormDecimalFieldOptions } from 'formComponents/FormDecimalField';
import useRouterConfirmMessage from 'hooks/useRouterConfirmMessage';
import { logDifference } from 'utils/helper';
import { Textbaustein } from 'formComponents/lookups/Textbaustein';
import { SmRefType } from 'api/attachment';
import { LieferantenrechnungPositionen } from './LieferantenrechnungPositionen';
import { AddAttachments } from '../History/AddAttachments';
import { LinkedAttachmentList } from '../History/LinkedAttachmentList';

export interface LieferantenrechnungEditData {
    kNr?: number;
    rNr: string;
}

interface LieferantenrechnungEditProps {
    defaultKNr?: number;
    rNr: string; // 'neu' = new
    onClose: (refetch: boolean) => void;
    onReopen: (aNr: string) => void;
}

type ButtonAction = 'reopen' | 'copy' | 'delete';

const allowedStatus = [0, 1, 5, 6];

export const LieferantenrechnungEdit: React.FC<LieferantenrechnungEditProps> = ({
    defaultKNr,
    rNr,
    onClose,
    onReopen,
}) => {
    const [savingState, setSavingState] = React.useState(SavingState.notChanged);
    const [validationError, setValidationError] = React.useState<string | null>(null);
    const isMounted = useIsMounted();

    const [refetchOnClose, setRefetchOnClose] = React.useState(false);

    const [lieferantenrechnung, setLieferantenrechnung] = React.useState<Lieferantenrechnung | null>(
        getDefaultLieferantenrechnung(defaultKNr),
    );
    const dbLieferantenrechnung = useLieferantenrechnung(
        {
            nr: rNr,
            withAuftrag: true,
        },
        {
            enabled: rNr !== 'neu',
            onSuccess: (loadedLieferantenrechnung) => {
                setLieferantenrechnung(cloneDeep(loadedLieferantenrechnung));
                setSavingState(SavingState.notChanged);
            },
        },
    );

    const kunde = useKunde(
        {
            nr: lieferantenrechnung?.kNr,
            withAttachmentList: true,
        },
        {
            enabled: lieferantenrechnung?.kNr !== undefined,
            onSuccess: (newKunde) => {
                if (rNr === 'neu' && lieferantenrechnung) {
                    setLieferantenrechnung({
                        ...lieferantenrechnung,
                        zahlBed: newKunde?.zahlZielK || lieferantenrechnung.zahlBed,
                    });
                }
            },
        },
    );

    const lieferantenrechnungPositionList = useLieferantenrechnungPositionList(
        {
            rNr,
        },
        { enabled: rNr !== 'neu' },
    );

    // const [kunde, setKunde] = React.useState<Kunde | null>(null);
    const [showCopy, setShowCopy] = React.useState(false);
    const [showDelete, setShowDelete] = React.useState(false);
    const [changeStatusValue, setChangeStatusValue] = React.useState<number | null>(null);
    const [showAddAttachments, setShowAddAttachments] = React.useState(false);

    /*
    React.useEffect(() => {
        if (
            !kundeList.isLoading &&
            !kundeList.isFetching &&
            kundeList.data?.length === 1 &&
            kundeList.data[0].nr !== kunde?.nr
        ) {
            setKunde(kundeList.data[0]);
            if (rNr === 'neu' && lieferantenrechnung) {
                setLieferantenrechnung({
                    ...lieferantenrechnung,
                    zahlBed: kundeList?.data[0]?.zahlZielK || lieferantenrechnung.zahlBed,
                });
            }
        }
    }, [kunde, kundeList, rNr, lieferantenrechnung]);
     */

    /*
    React.useEffect(() => {
        if (!lieferantenrechnungList.isLoading && !lieferantenrechnungList.isFetching && lieferantenrechnung === null) {
            if (lieferantenrechnungList.data?.length === 1) {
                setLieferantenrechnung(cloneDeep(lieferantenrechnungList.data[0]));
                setSavingState(SavingState.notChanged);
            } else {
                // const now = moment();

                setLieferantenrechnung(getDefaultLieferantenrechnung(defaultKNr));
                setSavingState(SavingState.notSaved);
            }
        }
    }, [lieferantenrechnung, lieferantenrechnungList, defaultKNr, auth, kundeList]);
     */

    React.useEffect(() => {
        if (
            [SavingState.notSaved, SavingState.notChanged].includes(savingState) &&
            !dbLieferantenrechnung.isLoading &&
            !dbLieferantenrechnung.isFetching &&
            lieferantenrechnung !== null
        ) {
            if (!isEqual(dbLieferantenrechnung.data, lieferantenrechnung)) {
                setSavingState(SavingState.notSaved);
                logDifference('rechnung', lieferantenrechnung, dbLieferantenrechnung.data);
            } else if (savingState !== SavingState.notChanged) {
                setSavingState(SavingState.notChanged);
            }
        }
    }, [savingState, dbLieferantenrechnung, lieferantenrechnung]);

    const readOnly = React.useMemo(() => {
        return lieferantenrechnung?.status !== 0;
    }, [lieferantenrechnung?.status]);

    const validate = React.useCallback((): boolean => {
        if (lieferantenrechnung?.kNr === undefined) {
            setValidationError('Bitte wählen Sie einen Kunden aus.');
            return false;
        }
        if (validationError !== null) {
            setValidationError(null);
        }

        return true;
    }, [lieferantenrechnung, validationError]);

    const handleButtonAction = React.useCallback(
        (action?: ButtonAction) => {
            if (!lieferantenrechnung?.nr || !action) {
                return;
            }

            switch (action) {
                case 'reopen':
                    onReopen(lieferantenrechnung.nr);
                    break;
                case 'copy':
                    setShowCopy(true);
                    break;
                case 'delete':
                    setShowDelete(true);
                    break;
                default:
                    break;
            }
        },
        [lieferantenrechnung, onReopen],
    );

    const save = React.useCallback(
        (onFinish?: (nr: string) => void, status?: number) => {
            if (!lieferantenrechnung) {
                return;
            }

            const updatedLieferantenrechnung: Lieferantenrechnung =
                status === undefined ? lieferantenrechnung : { ...lieferantenrechnung, status };

            if (savingState !== SavingState.saving) {
                if (validate()) {
                    setSavingState(SavingState.saving);
                    if (rNr === 'neu') {
                        addLieferantenrechnung(updatedLieferantenrechnung)
                            .then((result) => {
                                setSavingState(SavingState.saved);
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    onReopen(result.newNr);
                                }
                                if (onFinish) {
                                    onFinish(result.newNr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    } else {
                        updateLieferantenrechnung(updatedLieferantenrechnung)
                            .then(() => {
                                setRefetchOnClose(true);
                                if (isMounted) {
                                    dbLieferantenrechnung.refetch().then();
                                    setSavingState(SavingState.notChanged);
                                }
                                if (onFinish) {
                                    onFinish(lieferantenrechnung.nr);
                                }
                            })
                            .catch((error) => {
                                console.error(error);
                                setSavingState(SavingState.error);
                            });
                    }
                }
            }
        },
        [lieferantenrechnung, savingState, validate, rNr, isMounted, onReopen, dbLieferantenrechnung],
    );

    const statusButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = allowedStatus.map((s) => ({
            key: s.toString(),
            text: LieferantenrechnungStatusLabel[s],
            onClick: () => setChangeStatusValue(s),
        }));

        return { items };
    }, []);

    const actionButtonMenuProps: IContextualMenuProps = React.useMemo(() => {
        const items = [];

        if (rNr !== 'neu' && lieferantenrechnung?.status === 0) {
            items.push({
                key: 'delete',
                text: 'Löschen',
                iconProps: { iconName: 'Delete' },
                onClick: () => handleButtonAction('delete'),
            });
        }

        return { items };
    }, [rNr, lieferantenrechnung?.status, handleButtonAction]);

    const routerConfirmMessage = useRouterConfirmMessage({
        isDirty: savingState === SavingState.notSaved,
    });

    const renderTextField = (column: keyof Lieferantenrechnung, label: string, options: FormTextFieldOptions = {}) => (
        <FormTextField
            value={(lieferantenrechnung?.[column] || '') as string}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || readOnly }}
            onChange={(newValue): void => {
                if (lieferantenrechnung) {
                    setLieferantenrechnung({
                        ...lieferantenrechnung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderDecimalField = (
        column: keyof Lieferantenrechnung,
        label: string,
        options: FormDecimalFieldOptions = {},
        ignoreReadOnly = false,
    ) => (
        <FormDecimalField
            value={(lieferantenrechnung?.[column] || '') as number}
            savingState={savingState}
            options={{ ...options, disabled: options.disabled || (readOnly && !ignoreReadOnly) }}
            onChange={(newValue): void => {
                if (options.onChange) {
                    options.onChange(newValue);
                } else if (lieferantenrechnung) {
                    setLieferantenrechnung({
                        ...lieferantenrechnung,
                        [column]: newValue,
                    });
                }
            }}
            label={label}
        />
    );

    const renderTextbaustein = (
        column: keyof Lieferantenrechnung,
        label: string,
        kz: number,
        allowEmpty: boolean,
        menuPlacement: 'bottom' | 'top' = 'bottom',
    ) => (
        <Textbaustein
            kz={kz}
            value={lieferantenrechnung?.[column] as number}
            disabled={savingState === SavingState.saving || readOnly}
            label={label}
            allowEmpty={allowEmpty}
            menuPlacement={menuPlacement}
            onChange={(newValue): void => {
                if (lieferantenrechnung) {
                    setLieferantenrechnung({
                        ...lieferantenrechnung,
                        [column]: parseInt(newValue, 10) || 0,
                    });
                }
            }}
        />
    );

    const renderDatePicker = (
        column: keyof Lieferantenrechnung,
        label: string,
        required = false,
        defaultNow = false,
    ) => {
        return (
            <FormDatePicker
                label={label}
                savingState={savingState}
                value={(lieferantenrechnung?.[column] as string) || (defaultNow ? moment().toISOString() : undefined)}
                onChange={(newValue) => {
                    if (lieferantenrechnung) {
                        setLieferantenrechnung({
                            ...lieferantenrechnung,
                            [column]: newValue,
                        });
                    }
                }}
                options={{
                    required,
                    disabled: readOnly,
                }}
            />
        );
    };

    const renderLieferantenrechnungStatus = () => (
        <LieferantenrechnungStatus
            value={lieferantenrechnung?.status?.toString() || '0'}
            disabled={savingState === SavingState.saving || readOnly}
            onChange={(newValue): void => {
                if (lieferantenrechnung) {
                    setLieferantenrechnung({
                        ...lieferantenrechnung,
                        status: parseInt(newValue, 10),
                    });
                }
            }}
        />
    );

    const renderKundeSelect = () => (
        <KundeSelect
            label="Lieferant"
            kNr={lieferantenrechnung?.kNr}
            name={kunde.data?.name1}
            disabled={savingState === SavingState.saving || readOnly || rNr !== 'neu'}
            onChange={(newKNr): void => {
                if (lieferantenrechnung && lieferantenrechnung.kNr !== newKNr) {
                    setLieferantenrechnung({
                        ...lieferantenrechnung,
                        kNr: newKNr,
                    });
                }
            }}
        />
    );

    const lieferantenrechnungGutschrift =
        (lieferantenrechnung?.sNetto || 0) < 0 ? 'Lieferantengutschrift' : 'Lieferantenrechnung';

    return (
        <>
            {routerConfirmMessage.render()}
            <Dialog
                hidden={false}
                title={`${lieferantenrechnungGutschrift} ${lieferantenrechnung?.nr || ''} - ${
                    kunde.data?.name1 || '...'
                } ${kunde.data?.name2 || ''} ${kunde.data?.nr ? '(' + kunde.data.nr + ')' : ''}`}
                dialogContentProps={{
                    type: DialogType.normal,
                }}
                modalProps={{
                    isBlocking: true,
                    dragOptions: {
                        moveMenuItemText: 'Move',
                        closeMenuItemText: 'Close',
                        menu: ContextualMenu,
                    },
                }}
                minWidth="95%"
                maxWidth="95%"
                onDismiss={() => {
                    onClose(refetchOnClose);
                }}
            >
                <form className={formStyles.form}>
                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                {renderKundeSelect()}
                                {kunde.data ? <OpenLinkIcon link={createKundeUrl(kunde.data.nr.toString())} /> : null}
                            </div>
                            <div className={formStyles.gridCell} />
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('nr', 'Rechnung-Nr.', {
                                        maxLength: 10,
                                        disabled: true,
                                    })}
                                </div>
                                <div className={formStyles.gridCell}>
                                    {renderTextField('auNr', 'Auftrag-Nr. des Lieferanten', { maxLength: 10 })}
                                </div>
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div className={formStyles.gridCell}>
                                    {renderDatePicker('datum', 'Ausstellungsdatum', true, true)}
                                </div>
                                <div className={formStyles.gridCell}>{renderDatePicker('datBis', 'Eingangsdatum')}</div>
                            </div>

                            <div className={formStyles.gridCell}>
                                {kunde.data?.attachmentList && rNr !== 'neu' && (
                                    <LinkedAttachmentList
                                        kNr={kunde.data.nr}
                                        smRefType={SmRefType.lieferantenrechnung}
                                        smRef={rNr}
                                        attachmentList={kunde.data.attachmentList}
                                        refetchKunde={() => kunde.refetch()}
                                        onAdd={() => setShowAddAttachments(true)}
                                    />
                                )}
                            </div>
                        </div>

                        <div className={formStyles.thirdColumn}>
                            <div className={formStyles.gridCell}>{renderLieferantenrechnungStatus()}</div>
                            <div className={formStyles.gridCell}>
                                {renderTextbaustein('zahlBed', 'Zahlungsbedingungen', 12, true)}
                            </div>
                            <div className={formStyles.twoColumnsBlock}>
                                <div />
                                <div className={formStyles.gridCell}>
                                    {renderDecimalField('mwstCode', 'MwSt.', { fixed: 0, disabled: true })}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className={formStyles.threeColumnsBlock}>
                        <div className={formStyles.firstColumn}>
                            <div className={formStyles.gridCell}>
                                <FormTextarea
                                    value={kunde.data?.bemerkung || ''}
                                    label="Bemerkung (beim Kunden)"
                                    onChange={() => {}}
                                    savingState={savingState}
                                    options={{ disabled: true }}
                                />
                            </div>
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={kunde.data?.liefBem || ''}
                                label="Lieferbemerkung (beim Kunden)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                        <div className={formStyles.secondColumn}>
                            <FormTextarea
                                value={lieferantenrechnung?.auftrag?.zusatz || ''}
                                label="Bemerkung (beim Auftrag)"
                                onChange={() => {}}
                                savingState={savingState}
                                options={{ disabled: true }}
                            />
                        </div>
                    </div>

                    <div>
                        {lieferantenrechnung && lieferantenrechnungPositionList?.data && (
                            <LieferantenrechnungPositionen
                                lieferant={kunde.data || defaultKunde}
                                rNr={lieferantenrechnung.nr}
                                lieferantenrechnungPositionen={lieferantenrechnungPositionList?.data}
                                viewMode="long"
                                allowEdit={!readOnly && savingState === SavingState.notChanged}
                                refetch={async () => {
                                    const result = await recalculateLieferantenrechnung(lieferantenrechnung);
                                    if (result?.lieferantenrechnung) {
                                        dbLieferantenrechnung.refetch().then();
                                        setLieferantenrechnung({
                                            ...lieferantenrechnung,
                                            sNetto: result.lieferantenrechnung.sNetto,
                                            sBrutto: result.lieferantenrechnung.sBrutto,
                                            sMwst: result.lieferantenrechnung.sMwst,
                                        });
                                        lieferantenrechnungPositionList.refetch().then();
                                    }

                                    setRefetchOnClose(true);
                                }}
                            />
                        )}
                    </div>

                    {lieferantenrechnungPositionList?.data && (
                        <div className={formStyles.threeColumnsBlock}>
                            <div className={formStyles.firstColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Netto: {formatMoney(lieferantenrechnung?.sNetto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.secondColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe Brutto: {formatMoney(lieferantenrechnung?.sBrutto || 0)}
                                </div>
                            </div>
                            <div className={formStyles.thirdColumn}>
                                <div className={formStyles.sumLine}>
                                    Summe MwSt.: {formatMoney(lieferantenrechnung?.sMwst || 0)}
                                </div>
                            </div>
                        </div>
                    )}

                    <Timestamps
                        createdDate={lieferantenrechnung?.anlageDat}
                        createdDecimalTime={lieferantenrechnung?.anlageUhr}
                        createdUser={lieferantenrechnung?.anlageUser}
                        modifiedDate={lieferantenrechnung?.updDat}
                        modifiedDecimalTime={lieferantenrechnung?.updUhr}
                        modifiedUser={lieferantenrechnung?.updUser}
                    />

                    {validationError && <div className={formStyles.error}>{validationError}</div>}
                    {savingState === SavingState.error && (
                        <div className={formStyles.error}>Fehler beim Speichern.</div>
                    )}

                    <DialogFooter>
                        <DefaultButton
                            className={formStyles.copyButton}
                            onClick={() => handleButtonAction('copy')}
                            text="Kopieren"
                            disabled={savingState !== SavingState.notChanged || !lieferantenrechnung?.nr}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Status"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !lieferantenrechnung?.nr}
                            text="Status ändern"
                            menuProps={statusButtonMenuProps}
                            onClick={() => {}}
                        />
                        <DefaultButton
                            split
                            splitButtonAriaLabel="Weitere Aktionen"
                            aria-roledescription="action button"
                            disabled={savingState !== SavingState.notChanged || !lieferantenrechnung?.nr}
                            text="Weitere Aktionen"
                            menuProps={actionButtonMenuProps}
                        />
                        {savingState === SavingState.saving ? (
                            <PrimaryButton text="Speichere..." disabled />
                        ) : (
                            <PrimaryButton
                                onClick={() => save()}
                                text="Speichern"
                                disabled={savingState === SavingState.notChanged || dbLieferantenrechnung.isLoading}
                            />
                        )}
                    </DialogFooter>
                </form>
                {(dbLieferantenrechnung.isLoading ||
                    dbLieferantenrechnung.isFetching ||
                    kunde.isLoading ||
                    kunde.isFetching ||
                    !lieferantenrechnung) && <LoadingIndicator />}
            </Dialog>
            {showCopy && (
                <KundeSelect
                    label="Lieferant"
                    disabled={savingState === SavingState.saving || readOnly || rNr !== 'neu'}
                    dialogOnly
                    initFilterKNr={lieferantenrechnung?.kNr}
                    onChange={async (newKNr) => {
                        if (lieferantenrechnung && newKNr) {
                            const { newNr } = await copyLieferantenrechnung(
                                newKNr,
                                lieferantenrechnung,
                                lieferantenrechnungPositionList?.data,
                            );
                            setShowCopy(false);
                            onReopen(newNr);
                        }
                    }}
                    onCancel={() => setShowCopy(false)}
                />
            )}
            {showDelete && (
                <ConfirmMessage
                    title="Lieferantenrechnung löschen"
                    message={'Soll der Lieferantenrechnung ' + lieferantenrechnung?.nr + ' gelöscht werden?'}
                    onConfirm={() => {
                        if (lieferantenrechnung?.nr) {
                            deleteLieferantenrechnung(lieferantenrechnung).then(() => {
                                onClose(true);
                            });
                        }
                    }}
                    onCancel={() => setShowDelete(false)}
                />
            )}
            {changeStatusValue !== null && (
                <ConfirmMessage
                    title="Status ändern"
                    message={
                        'Soll der Status von Lieferantenrechnung ' +
                        lieferantenrechnung?.nr +
                        ' auf "' +
                        LieferantenrechnungStatusLabel[changeStatusValue] +
                        '" geändert werden?'
                    }
                    onConfirm={() => {
                        if (lieferantenrechnung?.nr) {
                            save(() => setChangeStatusValue(null), changeStatusValue);
                        }
                    }}
                    onCancel={() => setChangeStatusValue(null)}
                />
            )}
            {showAddAttachments && kunde.data && rNr !== 'neu' && (
                <AddAttachments
                    kNr={kunde.data.nr}
                    forcedSmRefType={SmRefType.lieferantenrechnung}
                    forcedSmRef={rNr}
                    onClose={(refetch) => {
                        setShowAddAttachments(false);
                        if (refetch) {
                            kunde.refetch();
                        }
                    }}
                />
            )}
        </>
    );
};
